import { Section } from '@/components/containers/section';
import { useTranslation } from '@/contexts/i18n-context';
import { useGetEncountersResponses } from '@/api/get-vq-responses';
import { Loader } from '@/components/loaders/loader';
import { DataRow, ExpandableInfo } from '@/components/encounter/review/expandable-info';
import { AnswerType, VariableResponse } from '@/api/api.schemas';
import { formatDate } from '@/util/formats';
import { groupBy } from 'lodash';
import { Photos, Files } from './condition-section-components';
import styles from './condition-section.module.css';
import reviewStyles from '@/styles/review-page.module.css';
import { useEncounter } from '@/contexts/encounter-context';
import clsx from 'clsx';

const translationBase = 'visit.review';

export function ConditionSection({ showEditButton = true }: { showEditButton?: boolean }) {
  const { t } = useTranslation();

  return (
    <Section
      className={reviewStyles.section}
      title={t(`${translationBase}.condition.title`)}
      dataTestId="review-section-condition"
    >
      <ConditionSectionContent showEditButton={showEditButton} />
    </Section>
  );
}

interface ConditionSectionContentProps {
  showEditButton?: boolean;
  sectionsClassName?: string;
}

export function ConditionSectionContent({
  showEditButton = true,
  sectionsClassName,
}: ConditionSectionContentProps) {
  const { t } = useTranslation();
  const { encounter } = useEncounter();
  const { data: responses, isFetching, error } = useGetEncountersResponses(encounter.id);

  return (
    <>
      {isFetching && <Loader type="small" />}
      {error && <p>{t(`${translationBase}.condition.error`)}</p>}
      {Object.entries(
        groupBy(responses?.variableResponses, (response) => response.reviewTitle),
      ).map(([title, groupedResponses]) => {
        const sortedResponses = [...groupedResponses].sort(responseSorter);

        return (
          <ExpandableInfo
            key={title}
            className={clsx(styles.expandableInfo, sectionsClassName)}
            title={title}
            {...(showEditButton && {
              editProps: {
                editTo: `/visit/${encounter.id}/questions/${groupedResponses[0].pageId}`,
                editModeParam: title,
              },
            })}
          >
            {sortedResponses.map((response) => {
              if (response.answerType === AnswerType.photoUpload) {
                return <Photos key={response.questionId} />;
              }

              if (response.answerType === AnswerType.file) {
                return <Files key={response.questionId} />;
              }

              return (
                <DataRow key={response.questionId} title={response.labelText ?? t('common.na')}>
                  {getResponseDisplay(response, t('visit.review.none'))}
                  {response.responseOther && (
                    <div className={styles.freeTextInput}>{response.responseOther}</div>
                  )}
                </DataRow>
              );
            })}
          </ExpandableInfo>
        );
      })}
    </>
  );
}

function getResponseDisplay(response: VariableResponse, defaultValue: string) {
  if (
    !(response.textResponses.multiple && response.textResponses.multiple.length > 0) &&
    !response.textResponses.single?.trim() &&
    !response.responseOther?.trim()
  ) {
    return <p>{defaultValue}</p>;
  }

  switch (response.answerType) {
    case AnswerType.dropdown:
    case AnswerType.radio:
    case AnswerType.checkboxOther:
      return response.textResponses.multiple?.map((text) => (
        <p key={text} className={reviewStyles.chip}>
          {text}
        </p>
      ));
    case AnswerType.datePast:
      return formatDate(response.textResponses.single, { format: 'user' });
    case AnswerType.datePastWithOption:
      return response.textResponses.multiple?.map((text) =>
        formatDate(text, {
          format: 'user',
          defaultValue: text,
        }),
      );
    case AnswerType.yesNo:
      switch (response.textResponses.single) {
        case 'yes':
          return <p className={reviewStyles.chip}>Yes</p>;
        case 'no':
          return <p className={reviewStyles.chip}>No</p>;
        case 'na':
          return <p className={reviewStyles.chip}>Not applicable</p>;
        default:
          return <p className={reviewStyles.chip}>{response.textResponses.single}</p>;
      }
    case AnswerType.bodyLocation:
    case AnswerType.slider10:
      return <p className={reviewStyles.chip}>{response.textResponses.single}</p>;
    default:
      return response.textResponses.single;
  }
}

function responseSorter(a: VariableResponse, b: VariableResponse) {
  if (a.answerType === AnswerType.photoUpload) return -1;
  if (b.answerType === AnswerType.photoUpload) return 1;
  if (a.answerType === AnswerType.file) return 1;
  if (b.answerType === AnswerType.file) return -1;
  return 0;
}
